// apps/spa/src/routes/CommandEdit.tsx
// /commands/:id/edit — Edit existing slash command (CMD-02 + CMD-03).
//
// Fetches the command via useCommand(id), populates CommandEditorForm defaultValues.
// While loading: Skeleton placeholder.
// On 404: 'Command not found' + back link.

import { Link, useParams } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { ApiError } from '@/lib/api';
import { CommandEditorForm } from '@/components/commands/CommandEditorForm';
import { useCommand } from '@/hooks/useCommands';

export function CommandEdit() {
  const { id } = useParams<{ id: string }>();
  const commandQuery = useCommand(id);

  if (commandQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (commandQuery.error) {
    const apiError =
      commandQuery.error instanceof ApiError ? commandQuery.error : null;
    if (apiError && apiError.status === 404) {
      return (
        <div className="space-y-4 py-8 text-center">
          <h2 className="font-display text-2xl">Command not found</h2>
          <p className="text-muted-foreground text-sm">
            This command may have been deleted by another officer.
          </p>
          <Link
            to="/commands"
            className="text-foreground inline-flex items-center text-sm underline"
          >
            <ChevronLeft className="size-4" /> Back to commands
          </Link>
        </div>
      );
    }
    return (
      <div className="space-y-4 py-8 text-center">
        <h2 className="font-display text-2xl">Could not load command</h2>
        <Link
          to="/commands"
          className="text-foreground inline-flex items-center text-sm underline"
        >
          <ChevronLeft className="size-4" /> Back to commands
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Link
        to="/commands"
        className="text-muted-foreground hover:text-foreground inline-flex items-center text-sm transition-colors"
      >
        <ChevronLeft className="size-4" />
        Back to commands
      </Link>
      <CommandEditorForm
        mode="edit"
        commandId={id}
        command={commandQuery.data}
      />
    </div>
  );
}
